import useAssetEventDisplayList from "hooks/useAssetEventDisplayList";
import { endsWith, isEmpty, startsWith, trim } from "lodash";
import React, { ReactNode } from "react";
import { ASSET_EVENTS_MAP } from "utils/assetUtils";
import { CellRenderers } from "utils/tableUtils";

import ACHistoryEvent from "ac-components/ac-history/ACHistoryEvent";

import { useAssetsDetails } from "./AssetsDetailsProvider";
import { HistoryResponse } from "./asset-history";

const DISPLAY_NOTE_EVENTS = [
  ASSET_EVENTS_MAP.CORRELATED_ASSET_FOUND,
  ASSET_EVENTS_MAP.ASSET_DELETED,
  ASSET_EVENTS_MAP.ASSET_REAPPEARED,
  ASSET_EVENTS_MAP.ASSET_CREATED_FROM_FINDINGS,
  ASSET_EVENTS_MAP.IMAGE_VERSION_ADDED,
];

const AssetHistorySection = ({ assetID }: { assetID: number }) => {
  const { history } = useAssetsDetails();
  const eventDisplay = useAssetEventDisplayList();

  const DISPLAY_EVENT_DETAILS: {
    [key: string]: (item: HistoryResponse) => ReactNode;
  } = {
    [ASSET_EVENTS_MAP.ACTIVE_UPDATE]: (item) => {
      const label = {
        true: "Active",
        false: "Inactive",
      };
      return renderOldToNewData(
        label[item.eventOldData as keyof typeof label],
        label[item.eventNewData as keyof typeof label],
      );
    },
  };

  const renderHistory = (item: HistoryResponse) => {
    const defaultHistoryHandler = (item: HistoryResponse) => (
      <>
        <div>{renderOldToNewData(item.eventOldData, item.eventNewData)}</div>
        <div>
          {DISPLAY_NOTE_EVENTS.indexOf(item.event) !== -1 && !isEmpty(item.note)
            ? item.note
            : ""}
        </div>
      </>
    );
    return DISPLAY_EVENT_DETAILS[item.event]
      ? DISPLAY_EVENT_DETAILS[item.event](item)
      : defaultHistoryHandler(item);
  };

  return (
    <ACHistoryEvent
      eventDisplay={eventDisplay}
      history={history}
      renderHistory={renderHistory}
    />
  );
};

export default AssetHistorySection;

const formatString = (str?: string) =>
  startsWith(str, "[") && endsWith(str, "]")
    ? trim(str, "[]").split(",").join(", ")
    : str;

const renderOldToNewData = (eventOldData?: string, eventNewData?: string) => {
  const displayValue = (value?: string) =>
    CellRenderers.VALUE_OR_NA(formatString(value));
  return (
    <>
      {displayValue(eventOldData)} &rarr;{" "}
      <span style={{ fontWeight: 800 }}>{displayValue(eventNewData)}</span>
    </>
  );
};
